// features/ups/modals/AgencyDrawer.js
// Right-side slide-over showing an agency’s contextual details (Accounts tab now; Settings placeholder)
/*Phase 8 agency management drawer. Renders a right-side slide-over for a selected agency, providing contextual management without leaving the Manage Agencies table. Includes:
- Contracts tab (default): UPS-admin contract control plane for the agency, allowing contract creation, enable/disable, and per-site permission management.
- Accounts tab: Lists agency user accounts (agency_* roles only) with delete actions.
- Settings tab: Reserved placeholder for future agency configuration.*/ 

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, MoreHorizontal } from 'lucide-react';

import {
  fetchAccounts,
  deleteAccount as deleteAccountThunk,
} from '../../agencyAccounts/agencyAccountsSlice';

import { loadMyCompanySites, selectCompanySites } from '../../sites/sitesSlice';

import {
  fetchContracts,
  createContract,
  setContractStatus,
  fetchContractSites,
  setContractSiteStatus,
  selectContractForAgency,
  selectContractSitesByContractId,
  selectContractsState,
  clearContractsErrors,
} from '../../contracts/contractsSlice';

// Small inline kebab menu
function RowMenu({ onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center h-9 w-9 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal size={18} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-1 w-40 rounded-md border bg-white shadow-lg overflow-hidden z-50"
        >
          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onDelete?.();
            }}
            className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default function AgencyDrawer({ isOpen, onClose, agency, onAddAccount }) {
  const dispatch = useDispatch();
  const { list, loading, error } = useSelector((s) => s.agencyAccounts);

  // 1) Default tab is Contracts (Phase 8 permission plane)
  const [tab, setTab] = useState('contracts'); // 'contracts' | 'accounts' | 'settings'

  const agId = agency?.id;
  const agName = agency?.name;

  // Contracts + sites selectors
  const sites = useSelector(selectCompanySites);
  const contractsState = useSelector(selectContractsState);
  const contract = useSelector(selectContractForAgency(agId));
  const contractSites = useSelector(selectContractSitesByContractId(contract?.id));

  // Contracts UI-only state
  const [editSitesOpen, setEditSitesOpen] = useState(false);
  const [draftSiteStatus, setDraftSiteStatus] = useState({});

  // Fetch accounts when drawer opens (Accounts tab uses this cache)
  useEffect(() => {
    if (!isOpen) return;
    if (!agId && !agName) return;
    dispatch(fetchAccounts({ agencyId: agId }));
  }, [dispatch, isOpen, agId, agName]);

  // 2) Load contracts + sites when Contracts tab opens
  useEffect(() => {
    if (!isOpen) return;
    if (tab !== 'contracts') return;
    if (!agId) return;

    dispatch(clearContractsErrors());
    dispatch(loadMyCompanySites());
    dispatch(fetchContracts());
  }, [dispatch, isOpen, tab, agId]);

  // Load contractSites rows for this contract when contract exists
  useEffect(() => {
    if (!isOpen) return;
    if (tab !== 'contracts') return;
    if (!contract?.id) return;

    dispatch(fetchContractSites({ contractId: contract.id }));
  }, [dispatch, isOpen, tab, contract?.id]);

  // Build local draft map when contractSites changes (checkbox UI)
  useEffect(() => {
    if (!contract?.id) return;

    const next = {};
    for (const r of contractSites || []) {
      const sid = r.site_id ?? r.siteId ?? r.id;
      const status = r.status ?? (r.allowed ? 'active' : 'disabled');
      if (sid != null) next[String(sid)] = status;
    }
    setDraftSiteStatus(next);
  }, [contract?.id, contractSites]);

  // Reset UI-only state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setTab('contracts'); // 1) Reset to Contracts, not Accounts
      setEditSitesOpen(false);
      setDraftSiteStatus({});
    }
  }, [isOpen]);

  // Client-side filter to ensure only this agency’s accounts show
  const rows = useMemo(() => {
    let r = list || [];
    if (agId || agName) {
      const idStr = agId != null && agId !== '' ? String(agId) : null;
      const nameStr = agName ? agName.trim().toLowerCase() : null;

      r = r.filter((acc) => {
        const accId = acc.agencyId ?? acc.agency_id ?? acc.agency?.id;
        const accName = (acc.agency_name ?? acc.agency?.name ?? '').trim().toLowerCase();
        if (idStr && accId != null && String(accId) === idStr) return true;
        if (nameStr && accName && accName === nameStr) return true;
        return false;
      });

      // Hide UPS roles in agency-scoped view
      r = r.filter((acc) => acc.role?.startsWith('agency'));
    }
    return r;
  }, [list, agId, agName]);

  // Helpers
  const allowedCount = useMemo(() => {
    return Object.values(draftSiteStatus || {}).filter((s) => s === 'active').length;
  }, [draftSiteStatus]);

  const onCreateContract = () => {
    if (!agId) return;
    dispatch(createContract({ agencyId: agId }));
  };

  const onToggleSiteDraft = (siteId) => {
    const key = String(siteId);
    setDraftSiteStatus((prev) => ({
      ...prev,
      [key]: prev[key] === 'active' ? 'disabled' : 'active',
    }));
  };

  const onSaveSiteStatus = (siteId) => {
    if (!contract?.id) return;
    const key = String(siteId);
    const status = draftSiteStatus[key] ?? 'disabled';
    dispatch(setContractSiteStatus({ contractId: contract.id, siteId, status }));
  };

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 z-40">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[520px] md:w-[640px] bg-white shadow-xl flex flex-col">
        {/* Header (sticky) */}
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
          <h3 className="text-lg font-semibold truncate">{agency?.name}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Tabs: Contracts, Accounts, Settings */}
        <div className="px-4 pt-3">
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setTab('contracts')}
              className={`px-3 py-1.5 rounded text-sm ${
                tab === 'contracts' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Contract
            </button>

            <button
              onClick={() => setTab('accounts')}
              className={`px-3 py-1.5 rounded text-sm ${
                tab === 'accounts' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Accounts
            </button>

            <button
              onClick={() => setTab('settings')}
              className={`px-3 py-1.5 rounded text-sm ${
                tab === 'settings' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Settings
            </button>
          </div>
        </div>

        {/* Content (scrollable) */}
        <div className="px-4 pb-20 overflow-y-auto flex-1">
          {/* 3B) Contracts tab */}
          {tab === 'contracts' && (
            <div className="p-3 space-y-4">
              {/* Contract card */}
              <div className="border rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold">Contract</div>
                    <div className="text-sm text-gray-600">
                      Controls whether this agency is permitted (and where).
                    </div>
                  </div>

                  {contract ? (
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        contract.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {contract.status === 'active' ? 'Active' : 'Disabled'}
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700">
                      No contract
                    </span>
                  )}
                </div>

                {(contractsState.loading || contractsState.sitesLoading) && (
                  <div className="text-gray-500 mt-3 text-sm">Loading…</div>
                )}

                {(contractsState.error || contractsState.sitesError || contractsState.mutateError) && (
                  <div className="text-red-600 mt-3 text-sm">
                    {contractsState.error || contractsState.sitesError || contractsState.mutateError}
                  </div>
                )}

                <div className="mt-4 space-y-3">
                  {/* Allowed sites */}
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <div className="font-medium">Allowed sites</div>
                      <div className="text-gray-600">{contract ? `${allowedCount} allowed` : '—'}</div>
                    </div>

                    <button
                      disabled={!contract}
                      onClick={() => setEditSitesOpen(true)}
                      className={`px-3 py-2 rounded text-sm border ${
                        contract ? 'hover:bg-gray-50' : 'opacity-50 cursor-not-allowed'
                      }`}
                    >
                      Edit sites
                    </button>
                  </div>

                  {/* Contract status */}
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <div className="font-medium">Contract status</div>
                      <div className="text-gray-600">
                        {contract ? 'Enable/disable this agency globally' : 'Create a contract to enable this agency'}
                      </div>
                    </div>

                    {contract ? (
                      <button
                        disabled={contractsState.mutateLoading}
                        onClick={() => {
                          const next = contract.status === 'active' ? 'disabled' : 'active';
                          dispatch(setContractStatus({ contractId: contract.id, status: next }));
                        }}
                        className="px-3 py-2 rounded text-sm bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-60"
                      >
                        {contract.status === 'active' ? 'Disable' : 'Enable'}
                      </button>
                    ) : (
                      <button
                        disabled={contractsState.mutateLoading}
                        onClick={onCreateContract}
                        className="px-3 py-2 rounded text-sm bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-60"
                      >
                        Create contract
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Edit sites modal (simple) */}
              {editSitesOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div className="absolute inset-0 bg-black/30" onClick={() => setEditSitesOpen(false)} />
                  <div className="relative bg-white w-full max-w-lg mx-4 rounded-lg shadow-xl border">
                    <div className="flex items-center justify-between p-4 border-b">
                      <div className="font-semibold">Edit allowed sites</div>
                      <button
                        onClick={() => setEditSitesOpen(false)}
                        className="p-1 rounded hover:bg-gray-100"
                        aria-label="Close"
                      >
                        <X size={18} />
                      </button>
                    </div>

                    <div className="p-4 max-h-[60vh] overflow-y-auto space-y-2">
                      {!sites?.length ? (
                        <div className="text-sm text-gray-500">No sites loaded.</div>
                      ) : (
                        sites.map((s) => {
                          const sid = s.id ?? s.site_id ?? s.siteId;
                          const key = sid != null ? String(sid) : '';
                          const status = key ? draftSiteStatus[key] ?? 'disabled' : 'disabled';
                          const isActive = status === 'active';

                          return (
                            <div key={key || s.name} className="flex items-center justify-between gap-3 py-2 border-b">
                              <div className="min-w-0">
                                <div className="text-sm font-medium truncate">{s.name ?? `Site ${key}`}</div>
                                <div className="text-xs text-gray-500">{isActive ? 'Allowed' : 'Blocked'}</div>
                              </div>

                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => sid != null && onToggleSiteDraft(sid)}
                                  className="px-2 py-1 rounded text-sm border hover:bg-gray-50"
                                >
                                  Toggle
                                </button>
                                <button
                                  type="button"
                                  disabled={!contract?.id || contractsState.mutateLoading}
                                  onClick={() => sid != null && onSaveSiteStatus(sid)}
                                  className="px-2 py-1 rounded text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    <div className="p-4 border-t flex justify-end">
                      <button
                        onClick={() => setEditSitesOpen(false)}
                        className="px-3 py-2 rounded text-sm border hover:bg-gray-50"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Accounts tab */}
          {tab === 'accounts' && (
            <>
              {loading && <div className="text-gray-500 p-3">Loading…</div>}
              {error && <div className="text-red-600 p-3">{error}</div>}

              {!loading && !error && rows.length === 0 && (
                <div className="text-gray-500 p-3">No accounts for this agency</div>
              )}

              {!loading && !error && rows.length > 0 && (
                <div className="divide-y">
                  {rows.map((acc) => (
                    <div key={acc.id} className="flex items-center justify-between py-3">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{acc.username}</div>
                        <div className="text-sm text-gray-600 truncate">{acc.email}</div>
                      </div>
                      <div className="ml-3 flex items-center gap-2">
                        <span className="text-xs px-2 py-1 rounded bg-gray-100 whitespace-nowrap">
                          {acc.role}
                        </span>
                        <RowMenu onDelete={() => dispatch(deleteAccountThunk(acc.id))} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Settings tab (reserved) */}
          {tab === 'settings' && <div className="text-gray-500 p-3">Settings coming soon…</div>}
        </div>

        {/* Sticky footer action (Accounts-only) */}
        {tab === 'accounts' && (
          <div className="p-3 border-t bg-white sticky bottom-0">
            <button
              onClick={onAddAccount}
              className="w-full px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Add account
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
